import React, { useEffect, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { colors, fontFamily, fontSizes, fontWeights, radii, shadows } from '../../theme/tokens';
import { SITE } from '../../config/siteContent';
import { Button, Icon } from '../ui';
import { useIsDesktop, useIsMobile } from '../../hooks/useMediaQuery';

// Pages where the floating contact button stays out of the way
const HIDDEN_PATHS = ['/login', '/register', '/checkout'];

// Height of the fixed bottom action bars on cart / grade books (padding + button), so the button sits above them
const BOTTOM_BAR_OFFSET = 88;

const PANEL_ID = 'contact-fab-panel';

export const WhatsAppLogo = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const ContactFab = () => {
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const fabRef = useRef(null);
  const firstActionRef = useRef(null);

  // Close whenever the page changes
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        if (fabRef.current) fabRef.current.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKey);
    if (firstActionRef.current) firstActionRef.current.focus();
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!SITE.supportPhone || HIDDEN_PATHS.includes(pathname)) return null;

  const telNumber = SITE.supportPhone.replace(/[^\d+]/g, '');
  const whatsappUrl = 'https://wa.me/' + telNumber.replace(/\D/g, '');

  const hasBottomBar =
    (isMobile && pathname === '/cart') || (!isDesktop && !!matchPath('/grade/:gradeId', pathname));
  const edge = isMobile ? 16 : 24;
  const bottom = hasBottomBar
    ? `calc(${BOTTOM_BAR_OFFSET + 12}px + env(safe-area-inset-bottom, 0px))`
    : `calc(${edge}px + env(safe-area-inset-bottom, 0px))`;
  const fabSize = isMobile ? 52 : 58;

  const close = () => {
    setOpen(false);
    if (fabRef.current) fabRef.current.focus();
  };

  return (
    <div
      ref={rootRef}
      style={{
        position: 'fixed', left: edge, bottom, zIndex: 900, fontFamily,
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12,
      }}
    >
      {open && (
        <section
          id={PANEL_ID}
          role="dialog"
          aria-label="Contact us"
          style={{
            boxSizing: 'border-box', width: 'min(340px, calc(100vw - 32px))', padding: '22px 20px 14px',
            borderRadius: radii.card, background: colors.white, border: '1px solid ' + colors.divider,
            boxShadow: shadows.modal, display: 'flex', flexDirection: 'column', gap: 10,
          }}
        >
          <h2 style={{ margin: 0, fontSize: fontSizes.h3, fontWeight: fontWeights.semibold, color: colors.ink }}>
            Contact us
          </h2>
          <p style={{ margin: 0, fontSize: fontSizes.body - 1, lineHeight: 1.5, color: colors.slate }}>
            In case of queries, please contact or WhatsApp:
          </p>
          <a
            href={'tel:' + telNumber}
            style={{ fontSize: fontSizes.bodyLg + 1, fontWeight: fontWeights.bold, color: colors.navy, textDecoration: 'none' }}
          >
            {SITE.supportPhone}
          </a>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
            <Button ref={firstActionRef} href={'tel:' + telNumber} variant="secondary" fullWidth>
              <Icon name="phone" size={18} />
              Call
            </Button>
            <Button
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
              style={{ background: colors.whatsapp, borderColor: colors.whatsapp, color: colors.white }}
            >
              <WhatsAppLogo size={18} />
              WhatsApp
            </Button>
            <Button variant="text" size="sm" fullWidth onClick={close} style={{ color: colors.slate }}>
              Cancel
            </Button>
          </div>
        </section>
      )}

      <button
        ref={fabRef}
        type="button"
        aria-label={open ? 'Close contact options' : 'Contact us'}
        aria-expanded={open}
        aria-controls={PANEL_ID}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: fabSize, height: fabSize, borderRadius: radii.pill, padding: 0, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: colors.whatsapp, color: colors.white,
          border: '3px solid ' + colors.white, boxShadow: '0 8px 24px rgba(22,138,71,0.32), 0 0 0 6px rgba(22,138,71,0.1)',
        }}
      >
        {open
          ? <Icon name="close" size={isMobile ? 24 : 26} strokeWidth={2} />
          : <WhatsAppLogo size={isMobile ? 26 : 30} />}
      </button>
    </div>
  );
};

export default ContactFab;
